/**
 * Split review rail (slice 3e) — how wide the page rail is, and what size its
 * thumbnails are RENDERED at.
 *
 * Persistence follows the Processing Monitor's column widths
 * (monitorColumnPrefs.js): a pure helper module, one localStorage key, every
 * read and write in a try/catch, a sanitising clamp so a corrupt or hand-edited
 * value can never lay the page out off-screen, and ONE commit when the drag
 * ends. The key is not per tenant: how wide a rail should be is a property of
 * the reviewer's screen, not of the workspace (`pm_default_tab` is the
 * un-tenanted precedent).
 *
 * WHY THE RENDER SIZE STEPS. The panel width is continuous, but re-rendering a
 * page is a full pdf.js decode of its image — about 150ms a page on the 56-page
 * HDS container whatever the output size. Tracking the width would re-render
 * every page for every pixel dragged. So the width picks one of RENDER_STEPS,
 * the SMALLEST that is at least as wide as the thumbnail is shown (never
 * upscaled below the top step), and nothing re-renders until that step changes.
 *
 * EVERY PAGE IS ONE SIZE. 3e showed a document's first page larger and the rest
 * at 60%; in use that made the rail ragged and left continuation pages unreadable
 * at any width. Which document a page belongs to is the grouping's job, not size.
 */

export const RAIL_WIDTH_KEY = 'sr_rail_width';
export const MIN_RAIL_WIDTH = 128;
export const MAX_RAIL_WIDTH = 640;
export const DEFAULT_RAIL_WIDTH = 280;

export const RENDER_STEPS = [120, 200, 300, 400];

/**
 * Horizontal space inside the rail that is not thumbnail: the rail's padding,
 * the document band's inset and the thumbnail wrapper's padding (see
 * SplitReviewPage.css). An estimate is enough — a step is chosen from it.
 */
export const RAIL_INSET = 36;

/** A pointer-derived width held inside the rail's bounds. Dragging past either end stops there. */
export function boundRailWidth(px) {
  return Math.min(MAX_RAIL_WIDTH, Math.max(MIN_RAIL_WIDTH, Math.round(px)));
}

/**
 * A STORED width: a positive finite number is bounded; anything else — garbage,
 * zero, negative — was never a width this page wrote, and reads as the default.
 */
export function clampRailWidth(px) {
  const n = Number(px);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_RAIL_WIDTH;
  return boundRailWidth(n);
}

// Reading window.localStorage can itself throw (blocked site data), so it is
// resolved inside each try rather than as a default parameter.
const browserStorage = () => (typeof window === 'undefined' ? undefined : window.localStorage);

export function loadRailWidth(storage) {
  try {
    const raw = (storage ?? browserStorage())?.getItem(RAIL_WIDTH_KEY);
    return raw == null ? DEFAULT_RAIL_WIDTH : clampRailWidth(raw);
  } catch {
    return DEFAULT_RAIL_WIDTH;
  }
}

/** Persist a width; returns the clamped width that was (or would have been) stored. */
export function saveRailWidth(px, storage) {
  const width = clampRailWidth(px);
  try {
    (storage ?? browserStorage())?.setItem(RAIL_WIDTH_KEY, String(width));
  } catch (err) {
    console.warn('[split-review] Failed to save the rail width:', err);
  }
  return width;
}

/** The smallest render step at least `displayPx` wide; the top step beyond it. */
export function renderStepFor(displayPx) {
  return RENDER_STEPS.find((step) => step >= displayPx) ?? RENDER_STEPS[RENDER_STEPS.length - 1];
}

/** The width a rail this wide shows every page at. */
export function displayWidthFor(railWidth) {
  return Math.max(1, clampRailWidth(railWidth) - RAIL_INSET);
}

/** The render step, in px, for every page in a rail this wide. */
export function renderWidthFor(railWidth) {
  return renderStepFor(displayWidthFor(railWidth));
}
